import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import IntranetLayout from './IntranetLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, XCircle, Users, CalendarPlus, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROGRAMMES = ['kids', 'neo', 'ruby', 'archy', 'elite'];

export const IntranetPresences = () => {
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [dates, setDates] = useState([]);
  const [stats, setStats] = useState({});
  const [filterProg, setFilterProg] = useState('kids');
  const [filterMonth, setFilterMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [groupDate, setGroupDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [groupProg, setGroupProg] = useState('kids');
  const [presentIds, setPresentIds] = useState([]);

  const fetchData = async () => {
    try {
      const [stRes, attRes, datesRes, statsRes] = await Promise.all([
        axios.get(`${API}/intranet/students`),
        axios.get(`${API}/intranet/attendance?programme=${filterProg}&month=${filterMonth}`),
        axios.get(`${API}/intranet/attendance/dates?programme=${filterProg}&month=${filterMonth}`),
        axios.get(`${API}/intranet/attendance/stats?programme=${filterProg}&month=${filterMonth}`),
      ]);
      setStudents(stRes.data);
      setAttendance(attRes.data);
      setDates(datesRes.data);
      setStats(statsRes.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchData(); }, [filterProg, filterMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const progStudents = useMemo(() => students.filter(s => s.programme === filterProg), [students, filterProg]);

  const getStatus = (studentId, date) => {
    const record = attendance.find(a => a.student_id === studentId && a.date === date);
    return record ? record.status : null;
  };

  const openGroupForm = () => {
    setGroupProg(filterProg);
    const progSt = students.filter(s => s.programme === filterProg);
    setPresentIds(progSt.map(s => s.id)); // all present by default
    setShowGroupForm(true);
  };

  const togglePresent = (id) => {
    setPresentIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/intranet/attendance/group`, {
        date: groupDate,
        programme: groupProg,
        present_ids: presentIds,
      });
      const groupStudents = students.filter(s => s.programme === groupProg);
      toast.success(`Pointage enregistré : ${presentIds.length}/${groupStudents.length} présents`);
      setShowGroupForm(false);
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const toggleIndividual = async (studentId, date, currentStatus) => {
    const newStatus = currentStatus === 'present' ? 'absent' : 'present';
    try {
      await axios.post(`${API}/intranet/attendance/individual`, {
        date,
        student_id: studentId,
        status: newStatus,
      });
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  // Month options
  const monthOptions = useMemo(() => {
    const options = [];
    const now = new Date();
    for (let i = -3; i <= 3; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      options.push({ value: val, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return options;
  }, []);

  // Format date for table header (just day)
  const formatDay = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.getDate();
  };
  const formatDayName = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('fr-FR', { weekday: 'short' }).slice(0, 3);
  };

  return (
    <IntranetLayout>
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="font-heading text-2xl font-extrabold text-slate-900">Présences</h2>
            <p className="text-sm text-slate-500">Pointage et suivi de la présence des élèves</p>
          </div>
          <Button onClick={openGroupForm} className="btn-primary" data-testid="add-group-attendance-btn">
            <CalendarPlus size={16} className="mr-1" /> Pointage groupe
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Select value={filterProg} onValueChange={setFilterProg}>
            <SelectTrigger className="w-40 rounded-xl" data-testid="filter-programme"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PROGRAMMES.map(p => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterMonth} onValueChange={setFilterMonth}>
            <SelectTrigger className="w-52 rounded-xl" data-testid="filter-month"><SelectValue /></SelectTrigger>
            <SelectContent>
              {monthOptions.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Stats summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-slate-100 p-4 text-center">
            <p className="text-2xl font-extrabold text-slate-900">{progStudents.length}</p>
            <p className="text-xs text-slate-500">Élèves {filterProg.toUpperCase()}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4 text-center">
            <p className="text-2xl font-extrabold text-slate-900">{dates.length}</p>
            <p className="text-xs text-slate-500">Séances ce mois</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4 text-center">
            <p className="text-2xl font-extrabold text-emerald-600">
              {progStudents.length > 0 ? Math.round(Object.values(stats).reduce((s, v) => s + (v.rate || 0), 0) / Math.max(Object.keys(stats).length, 1)) : 0}%
            </p>
            <p className="text-xs text-slate-500">Taux moyen</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4 text-center">
            <p className="text-2xl font-extrabold text-[#1B6FE3]">{Object.values(stats).filter(v => v.rate === 100).length}</p>
            <p className="text-xs text-slate-500">100% présence</p>
          </div>
        </div>

        {/* Group Attendance Modal */}
        {showGroupForm && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowGroupForm(false)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-heading text-lg font-extrabold">Pointage de groupe</h3>
                <button onClick={() => setShowGroupForm(false)}><X size={20} /></button>
              </div>
              <form onSubmit={handleGroupSubmit} className="space-y-4" data-testid="group-attendance-form">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold mb-1 block">Date *</Label>
                    <Input type="date" required value={groupDate} onChange={e => setGroupDate(e.target.value)} className="rounded-lg" data-testid="input-group-date" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold mb-1 block">Programme</Label>
                    <Select value={groupProg} onValueChange={v => {
                      setGroupProg(v);
                      const progSt = students.filter(s => s.programme === v);
                      setPresentIds(progSt.map(s => s.id));
                    }}>
                      <SelectTrigger className="rounded-lg" data-testid="select-group-prog"><SelectValue /></SelectTrigger>
                      <SelectContent>{PROGRAMMES.map(p => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-semibold">Élèves ({students.filter(s => s.programme === groupProg).length})</Label>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setPresentIds(students.filter(s => s.programme === groupProg).map(s => s.id))} className="text-xs text-emerald-600 font-bold hover:underline">Tous présents</button>
                      <button type="button" onClick={() => setPresentIds([])} className="text-xs text-red-500 font-bold hover:underline">Tous absents</button>
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                    {students.filter(s => s.programme === groupProg).map(s => (
                      <label key={s.id} className={`flex items-center justify-between px-3 py-2.5 cursor-pointer transition-colors ${presentIds.includes(s.id) ? 'bg-emerald-50' : 'bg-red-50/50'}`}>
                        <span className="text-sm font-medium text-slate-800">{s.prenom} {s.nom}</span>
                        <div className="flex items-center gap-2">
                          {presentIds.includes(s.id) ? (
                            <span className="text-xs font-bold text-emerald-600">Présent</span>
                          ) : (
                            <span className="text-xs font-bold text-red-500">Absent</span>
                          )}
                          <input type="checkbox" checked={presentIds.includes(s.id)} onChange={() => togglePresent(s.id)} className="rounded" />
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-3 text-center text-sm">
                  <span className="text-emerald-600 font-bold">{presentIds.length} présents</span>
                  <span className="text-slate-400 mx-2">|</span>
                  <span className="text-red-500 font-bold">{students.filter(s => s.programme === groupProg).length - presentIds.length} absents</span>
                </div>

                <Button type="submit" className="w-full btn-primary justify-center" data-testid="submit-group-attendance">
                  Enregistrer le pointage
                </Button>
              </form>
            </div>
          </div>
        )}

        {/* Monthly Recap Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          {progStudents.length === 0 || dates.length === 0 ? (
            <div className="p-10 text-center text-slate-400">
              {progStudents.length === 0 ? 'Aucun élève dans ce programme' : 'Aucune présence enregistrée ce mois'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 sticky left-0 bg-slate-50 z-10 min-w-[160px]">Élève</th>
                  {dates.map(d => (
                    <th key={d} className="text-center px-1 py-2 font-semibold text-slate-500 min-w-[40px]">
                      <div className="text-[10px] text-slate-400">{formatDayName(d)}</div>
                      <div className="text-xs">{formatDay(d)}</div>
                    </th>
                  ))}
                  <th className="text-center px-3 py-2 font-semibold text-slate-600 min-w-[60px]">Taux</th>
                </tr>
              </thead>
              <tbody>
                {progStudents.map(s => {
                  const st = stats[s.id] || { present: 0, total: 0, rate: 0 };
                  return (
                    <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`presence-row-${s.id}`}>
                      <td className="px-3 py-2 font-semibold text-slate-800 text-xs sticky left-0 bg-white z-10">
                        {s.prenom} {s.nom}
                      </td>
                      {dates.map(d => {
                        const status = getStatus(s.id, d);
                        return (
                          <td key={d} className="text-center px-1 py-2">
                            {status === 'present' ? (
                              <button onClick={() => toggleIndividual(s.id, d, status)} className="text-emerald-500 hover:text-emerald-700" title="Présent - cliquer pour changer">
                                <CheckCircle size={18} />
                              </button>
                            ) : status === 'absent' ? (
                              <button onClick={() => toggleIndividual(s.id, d, status)} className="text-red-400 hover:text-red-600" title="Absent - cliquer pour changer">
                                <XCircle size={18} />
                              </button>
                            ) : (
                              <span className="text-slate-200">-</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="text-center px-3 py-2">
                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                          st.rate >= 80 ? 'bg-emerald-100 text-emerald-700' :
                          st.rate >= 50 ? 'bg-amber-100 text-amber-700' :
                          st.total > 0 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-400'
                        }`}>
                          {st.total > 0 ? `${st.rate}%` : '-'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </IntranetLayout>
  );
};

export default IntranetPresences;
